import startCase from 'lodash/startCase';
import { ChangeDetails } from '../../types';

export interface FieldChange {
  key: string;
  label: string;
  before: string;
  after: string;
}

// Bookkeeping columns that change on every save and say nothing to the reader.
const IGNORED_KEYS = new Set(['id', 'createdAt', 'updatedAt', 'deletedAt', 'version']);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;
const NUMERIC = /^-?\d+(\.\d+)?$/;
const MAX_LIST_ITEMS = 5;
const MAX_RECORD_FIELDS = 3;

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isEmpty = (value: unknown) => value === null || value === undefined || value === '';

// A related record without an id means the relation was not loaded in that snapshot.
const isUnloadedRelation = (value: unknown) => isPlainObject(value) && 'id' in value && isEmpty(value.id);

// Reduces a value to something comparable: related records by id, dates by instant.
const normalize = (value: unknown): unknown => {
  if (isEmpty(value)) return null;
  if (isPlainObject(value)) return 'id' in value ? `#${value.id}` : JSON.stringify(value);
  // Order is not meaningful for rosters, so compare members as a set.
  if (Array.isArray(value)) return value.length ? JSON.stringify(value.map(normalize).map(String).sort()) : null;
  if (typeof value === 'string' && NUMERIC.test(value)) return Number(value);
  if (typeof value === 'string' && ISO_DATE.test(value)) {
    const time = Date.parse(value);
    return Number.isNaN(time) ? value : time;
  }
  return value;
};

const NAME_KEYS = ['fullName', 'displayName', 'name', 'title', 'label', 'email'];

// Names a record by who or what it is, falling back to its own fields when it has no name.
const describeRecord = (record: Record<string, any>): string => {
  const fullName = [record.firstName, record.middleName, record.lastName].filter(Boolean).join(' ');
  if (fullName) return fullName;
  const nameKey = NAME_KEYS.find((key) => typeof record[key] === 'string' && record[key]);
  if (nameKey) return record[nameKey];
  if (!isEmpty(record.id)) return `#${record.id}`;
  const fields = Object.entries(record)
    .filter(([key, value]) => !IGNORED_KEYS.has(key) && !isEmpty(value) && typeof value !== 'object')
    .slice(0, MAX_RECORD_FIELDS)
    .map(([key, value]) => `${startCase(key)}: ${formatAuditValue(value)}`);
  return fields.length ? `(${fields.join(', ')})` : '(empty)';
};

export const formatAuditValue = (value: unknown): string => {
  if (isEmpty(value)) return '(empty)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return '(empty)';
    const shown = value.slice(0, MAX_LIST_ITEMS).map(formatAuditValue).join(', ');
    return value.length > MAX_LIST_ITEMS ? `${shown} and ${value.length - MAX_LIST_ITEMS} more` : shown;
  }
  if (isPlainObject(value)) return describeRecord(value);
  if (typeof value === 'string' && ISO_DATE.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return value.includes('T') ? date.toLocaleString() : date.toLocaleDateString(undefined, { timeZone: 'UTC' });
    }
  }
  return String(value);
};

// A JSON column (object without an id) is diffed by its own keys, not as one value.
const isJsonColumn = (value: unknown) => isPlainObject(value) && !('id' in value);

// Top-level snapshots can omit columns that were not loaded, so only keys present in both
// are compared there. Inside a JSON column a missing key means the answer was added or removed.
const diffRecords = (
  before: Record<string, any>,
  after: Record<string, any>,
  keyPrefix = '',
  labelPrefix = '',
): FieldChange[] =>
  (keyPrefix
    ? Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
    : Object.keys(after).filter((key) => key in before)
  )
    .filter((key) => !IGNORED_KEYS.has(key))
    .filter((key) => !isUnloadedRelation(before[key]) && !isUnloadedRelation(after[key]))
    .flatMap((key) => {
      const label = `${labelPrefix}${startCase(key)}`;
      const [from, to] = [before[key], after[key]];
      if ((isJsonColumn(from) || isEmpty(from)) && (isJsonColumn(to) || isEmpty(to)) && !(isEmpty(from) && isEmpty(to))) {
        return diffRecords(from ?? {}, to ?? {}, `${keyPrefix}${key}.`, `${label}: `);
      }
      if (normalize(before[key]) === normalize(after[key])) return [];
      return [{
        key: `${keyPrefix}${key}`,
        label,
        before: formatAuditValue(before[key]),
        after: formatAuditValue(after[key]),
      }];
    });

// Lists the fields whose values differ between the before and after snapshots.
export const getFieldChanges = (changes?: ChangeDetails): FieldChange[] => {
  const before = changes?.before;
  const after = changes?.after;
  if (!before || !after) return [];
  return diffRecords(before, after);
};
